// src/hooks/usePortfolioData.js
//
// Portfolio-level data for the superuser dashboard, scoped to ONE client.
//
// Properties are nested under Clients/{clientId}/properties. For each property
// we listen to its pre-aggregated summary/rooms doc (maintained server-side by
// updatePropertySummary) plus its open work orders and property issues, then
// derive a health score. All per-property updates are merged into an id-keyed
// map via functional setState so listeners can't clobber each other (the bug
// FacilitiesDashboard has, where each snapshot rebuilds from a captured array).
//
// This is a client-side fan-out: fine for a client's portfolio of properties,
// and a drop-in candidate to later swap for a single Clients/{clientId}/
// portfolioSummary rollup doc without changing the dashboard.

import { useEffect, useMemo, useState } from "react";
import { onSnapshot, query } from "firebase/firestore";
import {
  clientDoc,
  clientPropertiesCollection,
  propertyDoc,
  propertyCollection,
} from "../utils/firestorePaths";
import {
  propertyHealth,
  healthBucket,
  DEFAULT_WEIGHTS,
} from "../utils/portfolioHealth";

const STATUS_KEYS = ["Up", "Down", "Occupied", "Blocked", "PM"];

const emptyMetrics = () => ({
  totalRooms: 0,
  statusBreakdown: {},
  downRooms: 0,
  needsVendor: 0,
  turnable: 0,
  lastUpdate: null,
  openWorkOrders: 0,
  openIssues: 0,
});

export default function usePortfolioData(clientId) {
  const [properties, setProperties] = useState([]); // raw property docs
  const [metricsById, setMetricsById] = useState({}); // { [id]: metrics }
  const [weights, setWeights] = useState(null); // client attentionWeights override
  const [loading, setLoading] = useState(true);

  // Client-level health-score weight overrides (Settings → Health Weights).
  // null = use portfolioHealth's built-in DEFAULT_WEIGHTS.
  useEffect(() => {
    if (!clientId) return;
    const unsub = onSnapshot(
      clientDoc(clientId),
      (doc) => setWeights(doc.data()?.attentionWeights || null),
      (err) => console.error("usePortfolioData: client doc listener", err)
    );
    return unsub;
  }, [clientId]);

  // 1) The client's property list (authoritative, includes location/address).
  useEffect(() => {
    if (!clientId) return;
    const unsub = onSnapshot(
      query(clientPropertiesCollection(clientId)),
      (snap) => {
        setProperties(
          snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
        setLoading(false);
      },
      (err) => {
        console.error("usePortfolioData: properties listener error", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [clientId]);

  // Helper: merge a partial metrics patch for one property id.
  const patch = (id, part) =>
    setMetricsById((prev) => ({
      ...prev,
      [id]: { ...emptyMetrics(), ...prev[id], ...part },
    }));

  // 2) Per-property listeners: summary/rooms + open work orders + open issues.
  useEffect(() => {
    if (!clientId || properties.length === 0) return;
    const unsubs = [];

    // Drop metrics for properties that no longer exist.
    setMetricsById((prev) => {
      const ids = new Set(properties.map((p) => p.id));
      const next = {};
      Object.keys(prev).forEach((id) => {
        if (ids.has(id)) next[id] = prev[id];
      });
      return next;
    });

    for (const p of properties) {
      const id = p.id;

      // summary/rooms — one light doc per property.
      unsubs.push(
        onSnapshot(
          propertyDoc(clientId, id, "summary", "rooms"),
          (doc) => {
            const s = doc.data() || {};
            const breakdown = s.statusBreakdown || {};
            patch(id, {
              totalRooms: s.totalRooms || 0,
              statusBreakdown: breakdown,
              downRooms: breakdown.Down || 0,
              needsVendor: s.needsVendor || 0,
              turnable: s.turnable || 0,
              lastUpdate: s.lastUpdated || null,
            });
          },
          (err) => console.error(`summary listener (${id})`, err)
        )
      );

      // Open work orders (whole collection, filtered client-side to tolerate
      // docs missing the `completed`/`markedCompleted` fields).
      unsubs.push(
        onSnapshot(
          query(propertyCollection(clientId, id, "workOrders")),
          (snap) => {
            const open = snap.docs.filter((d) => {
              const w = d.data();
              return w.completed === false && w.markedCompleted !== true;
            }).length;
            patch(id, { openWorkOrders: open });
          },
          (err) => console.error(`workOrders listener (${id})`, err)
        )
      );

      // Open property issues.
      unsubs.push(
        onSnapshot(
          query(propertyCollection(clientId, id, "propertyIssues")),
          (snap) => {
            const open = snap.docs.filter(
              (d) => d.data().completed === false
            ).length;
            patch(id, { openIssues: open });
          },
          (err) => console.error(`propertyIssues listener (${id})`, err)
        )
      );
    }

    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, properties.map((p) => p.id).join(",")]);

  // 3) Merge property docs + metrics into enriched rows with health scores.
  const rows = useMemo(() => {
    // Merge any client override over the built-in defaults so a partial
    // attentionWeights object can't produce NaN scores.
    const effectiveWeights = weights
      ? { ...DEFAULT_WEIGHTS, ...weights }
      : DEFAULT_WEIGHTS;
    return properties.map((p) => {
      const m = metricsById[p.id] || emptyMetrics();
      const health = propertyHealth(m, effectiveWeights);
      const bucket = healthBucket(health);
      const loc = p.location || p.geo || null;
      const lat = loc?.lat ?? loc?.latitude ?? null;
      const lng = loc?.lng ?? loc?.longitude ?? null;
      return {
        id: p.id,
        clientId,
        displayName: p.displayName || p.name || p.id,
        address: p.propertyAddress || "",
        lat,
        lng,
        hasLocation: typeof lat === "number" && typeof lng === "number",
        health,
        bucket,
        ...m,
      };
    });
  }, [properties, metricsById, clientId, weights]);

  // 4) Portfolio totals for the KPI row.
  const totals = useMemo(() => {
    const t = {
      properties: rows.length,
      rooms: 0,
      down: 0,
      occupied: 0,
      up: 0,
      needsVendor: 0,
      openWorkOrders: 0,
      openIssues: 0,
      attention: 0, // properties in Attention/Critical buckets
    };
    for (const r of rows) {
      t.rooms += r.totalRooms || 0;
      t.down += r.downRooms || 0;
      t.occupied += r.statusBreakdown?.Occupied || 0;
      t.up += r.statusBreakdown?.Up || 0;
      t.needsVendor += r.needsVendor || 0;
      t.openWorkOrders += r.openWorkOrders || 0;
      t.openIssues += r.openIssues || 0;
      if (r.health < 50) t.attention += 1;
    }
    return t;
  }, [rows]);

  // Ranked worst-first for the "Properties Needing Attention" table.
  const ranked = useMemo(
    () => [...rows].sort((a, b) => a.health - b.health),
    [rows]
  );

  return { rows, ranked, totals, loading, statusKeys: STATUS_KEYS };
}
